import CommonDatePicker from 'components/common/CommonDatePicker';
import DefaultText from 'components/common/DefaultText';
import LabelInput from 'components/common/LabelInput';
import LabelSelectBox from 'components/common/LabelSelectBox';
import LabelSelectButton from 'components/common/LabelSelectButton';
import {genderOptions, nationalityOptions} from 'data/option';
import {validateValueExtraInfo} from 'function/validate';
import moment from 'moment';
import {IRegisterInfo} from 'pages/Register';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';

/**
 * 추가 정보 컴포넌트의 Props 인터페이스
 * @property {IRegisterInfo} registerInfo - 등록 정보
 * @property {React.Dispatch<React.SetStateAction<IRegisterInfo>>} setRegisterInfo - 등록 정보 설정 함수
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setOnDatePicker - 날짜 선택기 열림 상태 설정 함수
 * @property {boolean} onDatePicker - 날짜 선택기 열림 여부
 * @property {React.RefObject<HTMLDivElement>} datePickerRef - 날짜 선택기 참조 객체
 * @property {React.Dispatch<React.SetStateAction<boolean>>} setEnableButton - 버튼 활성화 상태 설정 함수
 */
interface props {
  registerInfo: IRegisterInfo;
  setRegisterInfo: React.Dispatch<React.SetStateAction<IRegisterInfo>>;
  setOnDatePicker: React.Dispatch<React.SetStateAction<boolean>>;
  onDatePicker: boolean;
  datePickerRef: React.RefObject<HTMLDivElement>;
  setEnableButton: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * 추가 정보 컴포넌트
 * 이름, 생년월일, 성별, 국적 등의 추가 정보를 입력하는 컴포넌트입니다.
 * @param {props} props - 추가 정보 컴포넌트의 props
 * @returns {JSX.Element} 추가 정보 컴포넌트
 */
export default function ExtraInfo({
  registerInfo,
  setRegisterInfo,
  setOnDatePicker,
  onDatePicker,
  datePickerRef,
  setEnableButton,
}: props) {
  const errorTextStyle = {
    width: '100%',
    marginTop: '8px',
    fontSize: 'max(10px, min(1.5vw, 16px))',
    color: colors.primary,
    fontWeight: '400',
  };

  // 에러 메시지
  const [errorTexts, setErrorTexts] = useState({
    name: '',
    birthDay: '',
    gender: '',
    nationality: '',
  });

  useEffect(() => {
    const asyncFunc = async () => {
      const errorMsg = await validateValueExtraInfo({
        values: registerInfo,
        setErrorTexts,
      });
      if (
        registerInfo.name !== '' &&
        registerInfo.birthDay !== '' &&
        registerInfo.gender !== '' &&
        registerInfo.nationality !== '' &&
        errorMsg === ''
      ) {
        setEnableButton(true);
      } else {
        setEnableButton(false);
      }
    };
    asyncFunc();
  }, [registerInfo]);

  return (
    <>
      {/* 이름 */}
      <LabelInput
        labelText="name"
        type="text"
        value={registerInfo.name}
        setValue={(e: any) => setRegisterInfo({...registerInfo, name: e})}
        errorText={errorTexts.name}
        isColumn
      />

      {/* 생년월일 */}
      <div style={{position: 'relative'}}>
        <LabelInput
          labelText="birthDay"
          type="text"
          value={
            registerInfo.birthDay &&
            moment(registerInfo.birthDay).format('YYYY-MM-DD')
          }
          placeholder={!registerInfo.birthDay ? 'birthDayPlaceholder' : ''}
          isColumn
          readOnly
          rightImg="images/common/calender.png"
          onClickRightImg={() => {
            setOnDatePicker(!onDatePicker);
          }}
          onClickInput={() => {
            setOnDatePicker(!onDatePicker);
          }}
          inputStyle={{
            border: errorTexts.birthDay
              ? 'solid 1px rgb(230, 30, 22)'
              : 'solid 1px #cbcbcb',
          }}
        />
        <div
          style={{position: 'absolute', width: '70%', marginTop: minmax(10)}}>
          {onDatePicker && (
            <CommonDatePicker
              innerRef={datePickerRef}
              setOpen={setOnDatePicker}
              value={registerInfo.birthDay}
              setValue={(e: any) =>
                setRegisterInfo({...registerInfo, birthDay: e})
              }
              maxDate={new Date()}
            />
          )}
        </div>
        {errorTexts.birthDay && (
          <ErrorTextStyle>
            <DefaultText
              text={errorTexts.birthDay}
              textStyle={{...errorTextStyle}}
            />
          </ErrorTextStyle>
        )}
      </div>

      {/* 성별 */}
      <div>
        <LabelSelectButton
          labelText="gender"
          options={genderOptions}
          value={registerInfo.gender}
          setValue={(e: any) => setRegisterInfo({...registerInfo, gender: e})}
          isColumn
          errorMode={errorTexts.gender.length > 0}
          buttonStyle={{width: '100%'}}
        />
        {errorTexts.gender && (
          <ErrorTextStyle>
            <DefaultText
              text={errorTexts.gender}
              textStyle={{...errorTextStyle}}
            />
          </ErrorTextStyle>
        )}
      </div>

      {/* 국적 */}
      <div>
        <LabelSelectBox
          labelText="nationality"
          placeholder="nationalityPlaceholder"
          options={nationalityOptions}
          value={registerInfo.nationality}
          setValue={(e: any) =>
            setRegisterInfo({...registerInfo, nationality: e})
          }
          isColumn
          buttonStyle={{
            border: errorTexts.nationality ? '1px solid rgb(230, 30, 22)' : '',
          }}
        />
        {errorTexts.nationality && (
          <ErrorTextStyle>
            <DefaultText
              text={errorTexts.nationality}
              textStyle={{...errorTextStyle}}
            />
          </ErrorTextStyle>
        )}
      </div>
    </>
  );
}

const ErrorTextStyle = styled.div`
  width: 100%;
  margin-top: 8px;
  text-align: center;
  font-size: max(10px, min(1.5vw, 16px));
  color: ${colors.primary};
`;
